import React, { useState, useEffect } from 'react';
import { getData, postData, putData, deleteData, API_BASE_URL, API_TOKEN } from '../helpers/ApiSettings';
import WorkshopTable from '../components/Workshops/WorkshopTable';
import AddWorkshopModal from '../components/Workshops/AddWorkshopModal';
import EditWorkshopModal from '../components/Workshops/EditWorkshopModal';

const Workshops = () => {
  const [workshops, setWorkshops] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editingWorkshop, setEditingWorkshop] = useState(null);
  const [loading, setLoading] = useState(true);

  const [newWorkshop, setNewWorkshop] = useState({
    title: '',
    reference: '',
    short_description: '',
    description: '',
    program: '',
    prerequisites: '',
    workshop_instructions: '',
    price: null,
    duration: '',
    max_participants: 500,
    location: '',
    // available: true,
    category: '',
    dates: []
  });

  const fetchWorkshops = async () => {
    try {
      setLoading(true);
      const response = await getData('/workshops?populate=*');
      setWorkshops(response.data.data);
    } catch (error) {
      console.error('Erreur lors du chargement des workshops:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await getData('/workshop-categories');
      setCategories(response.data.data);
    } catch (error) {
      console.error('Erreur lors du chargement des catégories:', error);
    }
  };

  useEffect(() => {
    fetchWorkshops();
    fetchCategories();
  }, []);

  const handleAddWorkshop = async (e, workshopData) => {
    e.preventDefault();
    try {
      const { image, dates, ...workshopFields } = workshopData;
      const response = await postData('/workshops', { data: workshopFields });

      const workshopId = response.data.data.id;

      // Upload image
      if (image) await uploadImage(workshopId, image);

      // Create dates
      for (let d of dates) {
        await postData('/workshop-dates', {
          data: {
            ...d,
            date: d.date ? (() => {
              // Add seconds if needed for datetime-local format
              const dateStr = d.date.length === 16 ? d.date + ':00' : d.date;
              return new Date(dateStr).toISOString();
            })() : null,
            workshop: workshopId
          }
        });
      }

      setNewWorkshop({
        title: '',
        reference: '',
        short_description: '',
        description: '',
        program: '',
        prerequisites: '',
        workshop_instructions: '',
        price: '',
        duration: '',
        max_participants: 500,
        location: '',
        // available: true,
        category: '',
        dates: []
      });

      await fetchWorkshops();
      window.$('#addWorkshopModal').modal('hide');
      document.querySelector('.modal-backdrop')?.remove();
      alert('Atelier ajouté avec succès.');
    } catch (error) {
      console.error("Erreur lors de l'ajout de l'atelier:", error);
      alert("Erreur lors de l'ajout de l'atelier");
    }
  };

  const handleUpdateWorkshop = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        title: editingWorkshop.title,
        reference: editingWorkshop.reference,
        short_description: editingWorkshop.short_description,
        description: editingWorkshop.description,
        program: editingWorkshop.program,
        prerequisites: editingWorkshop.prerequisites,
        workshop_instructions: editingWorkshop.workshop_instructions,
        price: editingWorkshop.price || null,
        duration: editingWorkshop.duration,
        max_participants: editingWorkshop.max_participants,
        location: editingWorkshop.location,
        // available: editingWorkshop.available,
        category: typeof editingWorkshop.category === 'object' ? editingWorkshop.category.id : editingWorkshop.category
      };

      await putData(`/workshops/${editingWorkshop.documentId}`, { data: updateData });

      if (editingWorkshop.image && editingWorkshop.image instanceof File) {
        await uploadImage(editingWorkshop.id, editingWorkshop.image);
      }

      if (editingWorkshop.newDates?.length) {
        for (let d of editingWorkshop.newDates) {
          await postData('/workshop-dates', {
            data: {
              ...d,
              date: d.date ? (() => {
                // Add seconds if needed for datetime-local format
                const dateStr = d.date.length === 16 ? d.date + ':00' : d.date;
                return new Date(dateStr).toISOString();
              })() : null,
              workshop: editingWorkshop.id
            }
          });
        }
      }

      // TODO: implement updates to existing dates via PUT if needed

      // Update existing dates
      if (editingWorkshop.dates?.length) {
        for (let date of editingWorkshop.dates) {
          // Only update if the date has a valid ID and has been modified
          if (date.documentId) {
            await putData(`/workshop-dates/${date.documentId}`, {
              data: {
                date: date.date ? (() => {
                  // Check if date is already in ISO format or just datetime-local format
                  if (date.date.includes('Z') || date.date.length > 19) {
                    // Already in ISO format, use as is
                    return date.date;
                  } else if (date.date.includes(':')) {
                    // datetime-local format, add seconds if needed
                    const dateStr = date.date.length === 16 ? date.date + ':00' : date.date;
                    return new Date(dateStr).toISOString();
                  }
                  return date.date;
                })() : null,
                // start_time: date.start_time,
                // end_time: date.end_time,
                max_participants: date.max_participants,
                // available: date.available
              }
            });
          }
        }
      }

      setEditingWorkshop(null);
      await fetchWorkshops();
      window.$('#editWorkshopModal').modal('hide');
      document.querySelector('.modal-backdrop')?.remove();
      alert('Atelier mis à jour avec succès.');
    } catch (error) {
      console.error("Erreur lors de la mise à jour de l'atelier:", error);
      alert("Erreur lors de la mise à jour de l'atelier");
    }
  };

  const handleDeleteWorkshop = async (id) => {
    if (window.confirm('Supprimer cet atelier ?')) {
      try {
        await deleteData(`/workshops/${id}`);
        await fetchWorkshops();
        alert('Atelier supprimé');
      } catch (err) {
        console.error('Erreur suppression atelier:', err);
        alert('Erreur lors de la suppression');
      }
    }
  };

  const uploadImage = async (workshopId, file) => {
    const formData = new FormData();
    formData.append('ref', 'api::workshop.workshop');
    formData.append('refId', workshopId);
    formData.append('field', 'image');
    formData.append('files', file);

    const response = await fetch(`${API_BASE_URL}/api/upload`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${API_TOKEN}`
      },
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error?.message || "Échec de l'upload");
    }
  };

  const handleEditClick = (workshop) => {
    const copy = JSON.parse(JSON.stringify(workshop));
    copy.newDates = [];
    copy.dates = workshop.dates || [];
    setEditingWorkshop(copy);
    setTimeout(() => {
      window.$('#editWorkshopModal').modal('show');
    }, 100);
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="h4 mb-0">Workshops</h2>
        <button
          className="btn btn-primary"
          onClick={() => window.$('#addWorkshopModal').modal('show')}
        >
          Ajouter un atelier
        </button>
      </div>

      <div className="card">
        <div className="card-body">
          {loading ? (
            <div className="text-center p-4">
              <div className="spinner-border" role="status"></div>
            </div>
          ) : workshops.length === 0 ? (
            <p className="text-center text-muted">Aucun atelier disponible</p>
          ) : (
            <WorkshopTable
              workshops={workshops}
              onEdit={handleEditClick}
              onDelete={handleDeleteWorkshop}
            />
          )}
        </div>
      </div>

      <AddWorkshopModal
        newWorkshop={newWorkshop}
        setNewWorkshop={setNewWorkshop}
        onSubmit={handleAddWorkshop}
        categories={categories}
      />

      <EditWorkshopModal
        editingWorkshop={editingWorkshop}
        setEditingWorkshop={setEditingWorkshop}
        onSubmit={handleUpdateWorkshop}
        categories={categories}
      />
    </div>
  );
};

export default Workshops;